import type { MeResponse } from '@tvitvi/shared';
import { useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useSetActiveOrganization } from '@/api/queries/me';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { ICONS } from '@/components/ui/icons';
import { StatusTag } from '@/components/ui/StatusTag';
import { dynamicKey } from '@/i18n/types';
import { categoricalColorVar, initials } from '@/lib/format';

export function OrgSwitcher({ me }: { me: MeResponse }): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const switchOrganization = useSetActiveOrganization();

  const active = me.memberships.find(
    (membership) => membership.organizationId === me.activeOrganizationId,
  );
  const Chevron = ICONS.chevronDown;
  const Check = ICONS.check;

  /*
   * DET PERSONLIGE HUSET ER IKKE EN ORGANISASJON (sak 216), men det står i lista (sak 239.42, Atle
   * 04.10: «Vis den personlige»). Den som også er med i en organisasjon, kom seg ellers aldri tilbake
   * til sin egen kalender og sine egne oppgaver. Det står sist, med personens navn og «Personlig».
   * Står man i det, er toppraden personens eget navn uten rolle.
   *
   * EN FRYST ORGANISASJON STÅR SOM «FRYST» OG KAN IKKE VELGES (239.42). Før forsvant den helt, og
   * den som var med i den, trodde medlemskapet var borte.
   */
  const houses = me.memberships.filter((membership) => membership.organizationKind !== 'personal');
  const personalHouse = me.memberships.find(
    (membership) => membership.organizationKind === 'personal',
  );
  const frozen = me.frozenOrganizations ?? [];
  const personal = active?.organizationKind === 'personal';
  /*
   * GJESTEMODUS (sak 232, T5): organisasjonen som delte står aldri i lista — velgeren leser bare
   * medlemskapene — men toppraden sier hvor fanen står. Å velge et hus i lista går ut av gjestemodus.
   */
  const guestOrganization =
    me.activeOrgRole === 'guest'
      ? me.guestOrganizations.find((one) => one.organizationId === me.activeOrganizationId)
      : undefined;
  const title = personal
    ? me.person.displayName
    : (active?.organizationName ?? guestOrganization?.organizationName ?? null);

  if (personal && houses.length === 0 && frozen.length === 0) {
    return (
      <div
        className="flex h-[var(--topbar-height)] w-full items-center gap-2 px-2"
        data-testid="org-switcher-personal"
      >
        <span
          aria-hidden
          className="flex size-5 shrink-0 items-center justify-center rounded-xs text-2xs font-semibold text-inverse"
          style={{ backgroundColor: categoricalColorVar(me.person.id) }}
        >
          {initials(me.person.displayName)}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-default">
          {me.person.displayName}
        </span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        /* Sidemenyens topprad er navigasjon, ikke en handling. Se knappevakten. */
        data-unstyled
        aria-label={t('organizations.switcher.label')}
        className="flex h-[var(--topbar-height)] w-full items-center gap-2 rounded-[var(--radius-menu-row)] px-2 text-left ring-focus transition-colors duration-[var(--duration-fast)] hover:bg-[var(--fill-tonal)]"
      >
        <span
          aria-hidden
          className="flex size-5 shrink-0 items-center justify-center rounded-xs text-2xs font-semibold text-inverse"
          style={{
            backgroundColor: active
              ? categoricalColorVar(active.organizationId)
              : 'var(--color-border-strong)',
          }}
        >
          {title === null ? '—' : initials(title)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm leading-tight font-semibold text-default">
            {title ?? t('organizations.switcher.empty')}
          </span>
          {active && !personal ? (
            <span className="block truncate text-2xs leading-tight text-muted">
              {t(dynamicKey(`enums.org_role.${active.role}`))}
            </span>
          ) : guestOrganization ? (
            <span className="block truncate text-2xs leading-tight text-muted">
              {t('guest.bar.label')}
            </span>
          ) : null}
        </span>
        <Chevron aria-hidden className="size-3.5 shrink-0 text-muted" />
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <DropdownMenuLabel>{t('organizations.switcher.label')}</DropdownMenuLabel>
        {houses.map((membership) => (
          <DropdownMenuItem
            key={membership.organizationId}
            disabled={switchOrganization.isPending}
            /*
             * BYTTER MAN HUS, ER MAN ET ANNET STED (QA H4).
             *
             * Byttet lot ruta stå. Gikk man fra festivalens `/booking` til et byrå, sto byrået
             * igjen på en side som ikke finnes i menyen deres; gikk man motsatt vei fra
             * `/artists`, fikk festivalen byråets forespørselsliste med en forespørsel i.
             *
             * Data lakk ikke — RLS holdt, og rapporten bekreftet det — men skjermen viste et hus
             * man nettopp hadde forlatt. Rota er den ene adressen hvert hus har, og den sender
             * hver av dem videre til sin egen forside.
             */
            onSelect={() =>
              switchOrganization.mutate(membership.organizationId, {
                onSuccess: () => void navigate({ to: '/' }),
              })
            }
          >
            <span className="flex-1 truncate">{membership.organizationName}</span>
            {membership.organizationId === me.activeOrganizationId ? (
              <>
                <span className="sr-only">{t('organizations.switcher.current')}</span>
                <Check aria-hidden className="size-3.5 text-accent-text" />
              </>
            ) : null}
          </DropdownMenuItem>
        ))}
        {personalHouse === undefined ? null : (
          <DropdownMenuItem
            key={personalHouse.organizationId}
            disabled={switchOrganization.isPending}
            data-testid="org-switcher-personal-item"
            onSelect={() =>
              switchOrganization.mutate(personalHouse.organizationId, {
                onSuccess: () => void navigate({ to: '/' }),
              })
            }
          >
            <span className="flex-1 truncate">{me.person.displayName}</span>
            <span className="text-2xs text-muted">{t('organizations.switcher.personal')}</span>
            {personalHouse.organizationId === me.activeOrganizationId ? (
              <>
                <span className="sr-only">{t('organizations.switcher.current')}</span>
                <Check aria-hidden className="size-3.5 text-accent-text" />
              </>
            ) : null}
          </DropdownMenuItem>
        )}
        {frozen.map((organization) => (
          <DropdownMenuItem
            key={organization.id}
            disabled
            data-testid={`org-switcher-frozen-${organization.id}`}
          >
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate">{organization.name}</span>
              {/*
                F59 og 245.6: beskjeden til kunden, så den som er med i flere organisasjoner også ser
                hvorfor. En arkivert organisasjon kommer ikke hit (245.12, `app.my_frozen_notices`).
              */}
              {(organization.message ?? '') === '' ? null : (
                <span
                  className="truncate text-2xs text-subtle"
                  data-testid={`org-switcher-frozen-message-${organization.id}`}
                >
                  {t('organizations.switcher.frozenMessage', {
                    message: organization.message ?? '',
                  })}
                </span>
              )}
            </span>
            <StatusTag tone="danger">{t('organizations.switcher.frozen')}</StatusTag>
          </DropdownMenuItem>
        ))}
        {/*
          «NY ORGANISASJON» STO HER, OG DEN ER BORTE (sak B3).
          Atle: «Nye hus opprettes bare av Atle i gründerportalen, eller via invitasjonen "nytt
          hus" (sak 170).» Velgeren bytter mellom husene man ER i; den lager dem ikke. Sperren
          ligger på serveren — `OrganizationsService.claimHouseInvitation` — for en oppføring som
          bare er fjernet fra en meny er ikke fjernet fra API-et.
        */}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
